import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, Plus, Trash2, X, Shield, CheckCircle, Ban, Edit3, Key, MoreHorizontal, ChevronDown, BookOpen, CreditCard, FileText, User as UserIcon, Save, Loader2, MessageSquare, Crown, Eraser, Lock } from 'lucide-react';
import { userStore, User, QuestionPackage } from '../../store/userStore';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { usePaperValidation } from '../../hooks/usePaperValidation';
import { formatDateTime, formatDate } from '../../utils/timeUtils';
import { addLog } from '../../utils/operationLog';
import OrderList from '../../components/OrderList';
import PackageGrantModal from '../../components/PackageGrantModal';
import AuthConfirmModal from '../../components/AuthConfirmModal';

export default function MemberManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [members, setMembers] = useState<User[]>([]);
  const [studentTeacherMap, setStudentTeacherMap] = useState<Record<string, {id: string, username: string, phone: string}>>({});
  const [memberPointsMap, setMemberPointsMap] = useState<Record<string, number>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showBatchActionMenu, setShowBatchActionMenu] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<QuestionPackage | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<User | null>(null);
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [deleteMemberLoading, setDeleteMemberLoading] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [packageToToggle, setPackageToToggle] = useState<QuestionPackage | null>(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [toggleSuccess, setToggleSuccess] = useState(false);
  const [examRecords, setExamRecords] = useState<any[]>([]);
  const [examStats, setExamStats] = useState({ totalCount: 0, avgAccuracy: 0, recentStudyTime: '-' });
  const [detailRemark, setDetailRemark] = useState('');
  const [savingRemark, setSavingRemark] = useState(false);
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [basicInfoForm, setBasicInfoForm] = useState({ username: '', phone: '', status: 'enabled' });
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);
  const [bindTeacherInfo, setBindTeacherInfo] = useState<{id: string, username: string, phone: string} | null>(null);
  const [unbindingTeacher, setUnbindingTeacher] = useState(false);
  const [teacherBindForm, setTeacherBindForm] = useState({ teacherPhone: '', bindCode: '' });
  const [teacherBindLoading, setTeacherBindLoading] = useState(false);
  const [teacherBindError, setTeacherBindError] = useState('');
  const [teacherBindSuccess, setTeacherBindSuccess] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<'all' | 'paid' | 'free' | 'active' | 'inactive'>('all');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

  // 清除刷题记录相关状态
  const [showClearRecordsModal, setShowClearRecordsModal] = useState(false);
  const [clearingRecords, setClearingRecords] = useState(false);
  const [memberToClear, setMemberToClear] = useState<User | null>(null);
  const [clearRecordsPassword, setClearRecordsPassword] = useState('');
  const [clearRecordsError, setClearRecordsError] = useState('');
  const [adminIdForClear, setAdminIdForClear] = useState<string | undefined>(undefined);
  const DEFAULT_PASSWORD = '123456';
  const [editForm, setEditForm] = useState({ username: '', phone: '', role: 'student', remark: '' });
  const [addForm, setAddForm] = useState({ username: '', phone: '' });
  const [packageForm, setPackageForm] = useState({
    orgId: '',
    categoryId: '',
    levelId: '',
    packageType: '',
    price: 0,
    actualPrice: 0,
    expireDate: '',
    customDays: 30
  });
  const [showConfirmPackageModal, setShowConfirmPackageModal] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<CourseCategory[]>([]);
  const [availableLevels, setAvailableLevels] = useState<Level[]>([]);
  const { hasPaidPapers, isChecking, error: validationError, checkPaidPapers, resetValidation } = usePaperValidation();

  // U币管理相关状态
  const [showPointModal, setShowPointModal] = useState(false);
  const [pointMember, setPointMember] = useState<User | null>(null);
  const [pointBalance, setPointBalance] = useState<number>(0);
  const [pointLoading, setPointLoading] = useState(false);
  const [pointAction, setPointAction] = useState<'add' | 'deduct' | 'gift'>('add');
  const [pointAmount, setPointAmount] = useState<number>(0);
  const [pointRemark, setPointRemark] = useState('');
  const [pointSuccess, setPointSuccess] = useState(false);

  const packageTypeMap: Record<string, string> = {
    month: '月包',
    quarter: '季包',
    halfYear: '半年包',
    year: '年包',
    custom: '自定义包'
  };

  const getExpireDateByPackageType = (packageType: string, customDays?: number): string => {
    const today = new Date();
    const expireDate = new Date(today);
    let days = 30; // 默认30天
    switch (packageType) {
      case 'month':
        days = 30;
        break;
      case 'quarter':
        days = 90;
        break;
      case 'halfYear':
        days = 180;
        break;
      case 'year':
        days = 365;
        break;
      case 'custom':
        days = customDays || 30;
        break;
      default:
        days = 30;
    }
    expireDate.setDate(today.getDate() + days);
    return `${expireDate.getFullYear()}-${String(expireDate.getMonth() + 1).padStart(2, '0')}-${String(expireDate.getDate()).padStart(2, '0')}`;
  };

  // 加载学员列表（提升到组件作用域，供多处调用）
  const loadMembers = async () => {
    const users = await userStore.getUsers();
    setMembers(users);

    const studentIds = users.map(u => u.id);
    if (studentIds.length > 0) {
      // 一次性查询所有教师-学员绑定关系
      const { data: teacherStudentsData, error: tsError } = await supabase
        .from('teacher_students')
        .select('student_id, teacher_id')
        .in('student_id', studentIds);

      if (tsError) {
        console.error('查询教师-学员绑定关系失败:', tsError);
        setStudentTeacherMap({});
        return;
      }

      const allTeacherStudents = teacherStudentsData || [];

      if (allTeacherStudents.length > 0) {
        const teacherIdsFromBind = Array.from(new Set(allTeacherStudents.map(ts => ts.teacher_id)));
        const { data: teacherProfiles, error: tpError } = await supabase
          .from('profiles')
          .select('id, username, phone')
          .in('id', teacherIdsFromBind);

        if (tpError) {
          console.error('查询教师信息失败:', tpError);
        }

        const teacherProfileMap: Record<string, {id: string, username: string, phone: string}> = {};
        teacherProfiles?.forEach(t => {
          teacherProfileMap[t.id] = {
            id: t.id,
            username: t.username,
            phone: t.phone?.includes('@') ? t.phone.split('@')[0] : t.phone
          };
        });

        const studentTeacherBindMap: Record<string, {id: string, username: string, phone: string}> = {};
        allTeacherStudents.forEach(ts => {
          if (teacherProfileMap[ts.teacher_id]) {
            studentTeacherBindMap[ts.student_id] = teacherProfileMap[ts.teacher_id];
          }
        });
        setStudentTeacherMap(studentTeacherBindMap);
      } else {
        setStudentTeacherMap({});
      }
    }

    // 批量获取学员U币余额
    if (studentIds.length > 0) {
      try {
        const { data: pointAccounts } = await supabase
          .from('user_point_accounts')
          .select('user_id, total_points')
          .eq('role', 'student')
          .in('user_id', studentIds);

        if (pointAccounts) {
          const pointsMap: Record<string, number> = {};
          pointAccounts.forEach(account => {
            pointsMap[account.user_id] = account.total_points || 0;
          });
          setMemberPointsMap(pointsMap);
        }
      } catch (error) {
        console.error('获取学员U币余额失败:', error);
      }
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadMembers();

      // 如果 URL 包含 userId 参数，立即打开该学员的详情弹窗
      const userIdFromUrl = searchParams.get('userId');
      if (userIdFromUrl) {
        const users = await userStore.getUsers();
        const targetMember = users.find(u => u.id === userIdFromUrl);
        if (targetMember) {
          setSelectedMember(targetMember);
          setDetailRemark(targetMember.remark || '');
          setShowDetailModal(true);
          loadStudentDetailData(targetMember.id);
        }
      }
    };

    init();

    const handlePackageUpdate = () => {
      loadMembers();
    };
    window.addEventListener('teacher-package-updated', handlePackageUpdate);

    const handleTeacherDeleted = () => {
      loadMembers();
    };
    window.addEventListener('teacher-deleted', handleTeacherDeleted);

    const unsubscribe = userStore.subscribe(async () => {
      await loadMembers();
    });

    return () => {
      unsubscribe();
      window.removeEventListener('teacher-package-updated', handlePackageUpdate);
      window.removeEventListener('teacher-deleted', handleTeacherDeleted);
    };
  }, []);


  const isExpired = (dateStr: string) => new Date(dateStr) < new Date();

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      if (m.role !== 'student') return false;
      const matchSearch = m.username.toLowerCase().includes(searchTerm.toLowerCase()) || m.phone.includes(searchTerm);
      if (!matchSearch) return false;
      switch (filterType) {
        case 'paid': return m.isPaid;
        case 'free': return !m.isPaid;
        case 'active': return !m.isDisabled && !m.isBanned;
        case 'inactive': return m.isDisabled || m.isBanned;
        default: return true;
      }
    });
  }, [members, searchTerm, filterType]);

  const handleAddUser = () => {
    if (!addForm.username.trim() || !addForm.phone.trim()) return;
    userStore.addUser({
      username: addForm.username.trim(),
      phone: addForm.phone.trim(),
      isDisabled: false,
      isBanned: false,
      role: 'student',
      isPaid: false,
      packageName: '-',
      packageExpireDate: '-',
      remark: '',
      audit_status: 'pending',
    });
    setShowAddModal(false);
    setAddForm({ username: '', phone: '' });
  };

  const handleEdit = async () => {
    if (!selectedMember) return;
    const success = await userStore.updateUser(selectedMember.id, {
      username: editForm.username,
      phone: editForm.phone,
      role: editForm.role as 'student' | 'teacher',
      remark: editForm.remark,
    });
    setShowEditModal(false);
    if (success) {
      // 触发全局事件通知 Header 刷新用户信息
      window.dispatchEvent(new Event('user-info-updated'));
    }
  };

  const handleDelete = (member: User) => {
    setMemberToDelete(member);
    setShowDeleteMemberModal(true);
  };

  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;
    setDeleteMemberLoading(true);
    const result = await userStore.deleteUser(memberToDelete.id);
    setDeleteMemberLoading(false);
    if (result.success) {
      setShowDeleteMemberModal(false);
      // 局部更新：直接从列表中移除该学员
      setMembers(prev => prev.filter(m => m.id !== memberToDelete.id));
      setStudentTeacherMap(prev => { const next = {...prev}; delete next[memberToDelete.id]; return next; });
      setMemberToDelete(null);
    }
  };

  // 清除刷题记录相关函数
  const handleClearRecords = (member: User) => {
    setMemberToClear(member);
    setClearRecordsPassword('');
    setClearRecordsError('');
    setShowClearRecordsModal(true);
  };

  const confirmClearRecords = async () => {
    if (!memberToClear) return;
    if (!clearRecordsPassword.trim()) {
      setClearRecordsError('请输入鉴权密码');
      return;
    }
    setClearingRecords(true);
    setClearRecordsError('');
    try {
      // 后台管理员使用 admin_session（localStorage），没有 Supabase Auth session
      // 直接调用 exam-service 云函数，一步完成密码验证+清除记录
      const adminSession = localStorage.getItem('admin_session');
      let adminId: string | undefined;
      if (adminSession) {
        const sessionData = JSON.parse(adminSession);
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('id')
          .eq('username', sessionData.username)
          .maybeSingle();
        if (adminData) {
          adminId = adminData.id;
        }
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'clear_student_records',
          studentId: memberToClear.id,
          admin_id: adminId,
          password: clearRecordsPassword
        }),
      });

      const result = await response.json();
      if (result.success) {
        setShowClearRecordsModal(false);
        setMemberToClear(null);
        setClearRecordsPassword('');
        // 局部更新：重置学员的刷题次数和最近刷题时间
        setMembers(prev => prev.map(m =>
          m.id === memberToClear.id
            ? { ...m, studyCount: 0, recentStudyTime: null }
            : m
        ));
        // 广播事件通知答题记录页面刷新
        window.dispatchEvent(new CustomEvent('student-records-cleared', { detail: { studentId: memberToClear.id } }));
        alert('清除刷题记录成功');
      } else {
        setClearRecordsError(result.error || result.message || '操作失败');
      }
    } catch (error) {
      console.error('清除刷题记录失败:', error);
      setClearRecordsError('清除失败，请稍后重试');
    } finally {
      setClearingRecords(false);
    }
  };


  const handleBatchAction = (action: string) => {
    const ids = Array.from(selectedMembers);
    switch (action) {
      case 'enable':
        ids.forEach(id => userStore.updateUser(id, { isDisabled: false }));
        // 局部更新：直接更新列表中选中学员的状态
        setMembers(prev => prev.map(m => ids.includes(m.id) ? {...m, isDisabled: false} : m));
        break;
      case 'disable':
        ids.forEach(id => userStore.updateUser(id, { isDisabled: true }));
        // 局部更新：直接更新列表中选中学员的状态
        setMembers(prev => prev.map(m => ids.includes(m.id) ? {...m, isDisabled: true} : m));
        break;
      case 'delete':
        userStore.deleteUsers(ids);
        // 局部更新：直接从列表中移除选中的学员
        setMembers(prev => prev.filter(m => !ids.includes(m.id)));
        setStudentTeacherMap(prev => { const next = {...prev}; ids.forEach(id => delete next[id]); return next; });
        break;
      case 'export':
        const csv = [['用户名', '手机号', '注册时间', '最后登录', '角色', '状态', '付费状态', '套餐到期', '刷题次数', '备注'].join(','),
          ...ids.map(id => {
            const m = members.find(u => u.id === id)!;
            return [m.username, m.phone, formatDateTime(m.created_at), formatDateTime(m.last_login_time), m.role, m.isDisabled ? '禁用' : '启用', m.isPaid ? '付费' : '免费', formatDate(m.packageExpireDate), m.studyCount, m.remark].join(',');
          })].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `students_${(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}.csv`;
        link.click();
        break;
    }
    setSelectedMembers(new Set());
    setShowBatchActionMenu(false);
  };

  const handleAddPackage = async () => {
    if (!selectedMember || !packageForm.orgId || !packageForm.categoryId || !packageForm.levelId || !packageForm.packageType) return;
    const org = orgs.find(o => o.id === packageForm.orgId);
    const cat = cats.find(c => c.id === packageForm.categoryId);
    const level = levels.find(l => l.id === packageForm.levelId);

    const now = new Date();
    const existingActivePackage = selectedMember.questionPackages.find(p =>
      p.organization === org?.name &&
      p.category === cat?.name &&
      p.level_text === level?.name &&
      p.isActive &&
      new Date(p.expireDate) > now
    );
    if (existingActivePackage) {
      alert('该学员已开通此刷题包，请勿重复开通');
      return;
    }

    const result = await userStore.addQuestionPackage(selectedMember.id, {
      organization: org?.name || '',
      category: cat?.name || '',
      level_text: level?.name || '',
      expireDate: packageForm.expireDate,
      isActive: true,
    }, 'admin_grant', packageForm.price, packageForm.actualPrice, packageForm.packageType);

    if (!result.success) {
      alert('开通套餐失败');
      return;
    }

    setPackageForm({ orgId: '', categoryId: '', levelId: '', packageType: '', price: 0, actualPrice: 0, expireDate: '', customDays: 30 });
    resetValidation();
    setShowConfirmPackageModal(false);
    setShowPackageModal(false);
  };

  const handlePackageTypeChange = (packageType: string) => {
    const expireDate = getExpireDateByPackageType(packageType);
    setPackageForm(prev => ({ ...prev, packageType, expireDate }));
  };

  const openPackageModal = (member: User) => {
    setSelectedMember(member);
    setPackageForm({ orgId: '', categoryId: '', levelId: '', packageType: '', price: 0, actualPrice: 0, expireDate: '', customDays: 30 });
    setAvailableCategories([]);
    setAvailableLevels([]);
    resetValidation();
    setShowPackageModal(true);
  };

  // U币管理相关函数
  const openPointModal = async (member: User) => {
    setPointMember(member);
    setPointAction('add');
    setPointAmount(0);
    setPointRemark('');
    setPointSuccess(false);
    setShowPointModal(true);
    // 查询当前U币余额
    try {
      const { data: account } = await supabase
        .from('user_point_accounts')
        .select('total_points')
        .eq('user_id', member.id)
        .eq('role', 'student')
        .maybeSingle();
      setPointBalance(account?.total_points || 0);
    } catch {
      setPointBalance(0);
    }
  };

  const handlePointAction = async () => {
    if (!pointMember || pointAmount <= 0) {
      alert('请输入有效的U币数量');
      return;
    }

    setPointLoading(true);
    try {
      const adminUser = (await supabase.auth.getUser()).data.user;

      // 先确保学员有U币账户
      const { data: existingAccount } = await supabase
        .from('user_point_accounts')
        .select('id')
        .eq('user_id', pointMember.id)
        .eq('role', 'student')
        .maybeSingle();

      if (!existingAccount) {
        // 创建U币账户
        const createResponse = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'create_account',
            user_id: pointMember.id,
            role: 'student',
          }),
        });
        const createResult = await createResponse.json();
        if (!createResult.success) {
          alert(`创建U币账户失败: ${createResult.error || '未知错误'}`);
          setPointLoading(false);
          return;
        }
      }

      let action: string;
      let sourceType: string;
      let pointType: string;

      switch (pointAction) {
        case 'add':
          action = 'add_points';
          sourceType = 'admin_adjust';
          pointType = 'activity_reward';
          break;
        case 'deduct':
          action = 'deduct_points';
          sourceType = 'admin_adjust';
          pointType = 'activity_reward';
          break;
        case 'gift':
          action = 'add_points';
          sourceType = 'admin_gift';
          pointType = 'teacher_gift';
          break;
        default:
          action = 'add_points';
          sourceType = 'admin_adjust';
          pointType = 'activity_reward';
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          user_id: pointMember.id,
          role: 'student',
          points: pointAmount,
          point_type: pointType,
          source_type: sourceType,
          source_detail: pointRemark || `${pointAction === 'add' ? '管理员增加U币' : pointAction === 'deduct' ? '管理员扣减U币' : '管理员赠送U币'} ${pointAmount}`,
          operator_id: adminUser?.id,
          operator_role: 'admin',
          remark: pointRemark,
        }),
      });

      const result = await response.json();
      if (result.success || result.new_balance !== undefined) {
        setPointSuccess(true);
        // 更新余额显示
        const { data: account } = await supabase
          .from('user_point_accounts')
          .select('total_points')
          .eq('user_id', pointMember.id)
          .eq('role', 'student')
          .maybeSingle();
        setPointBalance(account?.total_points || 0);
        // 记录操作日志
        try {
          await supabase.from('admin_operation_logs').insert({
            admin_id: adminUser?.id || '',
            admin_name: (() => { try { const s = JSON.parse(localStorage.getItem('admin_session') || '{}'); return s.username || '管理员'; } catch { return '管理员'; } })(),
            admin_role: (() => { try { const s = JSON.parse(localStorage.getItem('admin_session') || '{}'); return s.role === 'super' ? 'super' : 'admin'; } catch { return 'admin'; } })(),
            operation_type: 'point_adjust',
            target_user_id: pointMember.id,
            target_user_name: pointMember.username,
            details: { action: pointAction, amount: pointAmount, remark: pointRemark },
            operation_desc: `${pointAction === 'add' ? '增加' : pointAction === 'deduct' ? '扣减' : '赠送'}U币 ${pointAmount}，备注：${pointRemark || '无'}`,
          });
        } catch (logError) {
          console.error('记录操作日志失败:', logError);
        }
      } else {
        alert(`操作失败: ${result.error || '未知错误'}`);
      }
    } catch (error) {
      console.error('U币操作失败:', error);
      alert('操作失败，请稍后重试');
    } finally {
      setPointLoading(false);
    }
  };

  const handleOrgChange = (orgId: string) => {
    setPackageForm(prev => ({ ...prev, orgId, categoryId: '', levelId: '', packageType: '', price: 0, actualPrice: 0, expireDate: '', customDays: 30 }));
    setAvailableCategories((cats || []).filter(c => c.orgId === orgId));
    setAvailableLevels([]);
    resetValidation();
  };

  const handleCategoryChange = (categoryId: string) => {
    setPackageForm(prev => ({ ...prev, categoryId, levelId: '', packageType: '', price: 0, actualPrice: 0, expireDate: '', customDays: 30 }));
    setAvailableLevels((levels || []).filter(l => l.catId === categoryId));
    resetValidation();
  };

  const cats = categoryStore.getCategories();
  const levels = categoryStore.getLevels();

  const handleLevelChange = (levelId: string) => {
    setPackageForm(prev => ({ ...prev, levelId, packageType: '', price: 0, actualPrice: 0, expireDate: '', customDays: 30 }));
    const org = cats && levels ? orgs.find(o => o.id === packageForm.orgId) : undefined;
    const cat = cats?.find(c => c.id === packageForm.categoryId);
    const lvl = levels?.find(l => l.id === levelId);
    if (org?.name && cat?.name && lvl?.name) {
      checkPaidPapers(org.name, cat.name, lvl.name);
    }
  };

  const filteredCats = (cats || []).filter(c => c.orgId === packageForm.orgId);
  const filteredLevels = (levels || []).filter(l => l.catId === packageForm.categoryId);

  const getPriceForPackageType = (packageType: string): number => {
    const lvl = levels.find(l => l.id === packageForm.levelId);
    if (!lvl) return 0;
    const price = categoryStore.getInstitutionPrices().find(p => p.levelId === lvl.id);
    if (!price) return 0;
    switch (packageType) {
      case 'month': return price.priceMonth || 0;
      case 'quarter': return price.priceQuarter || 0;
      case 'halfYear': return price.priceHalfYear || 0;
      case 'year': return price.priceYear || 0;
      default: return 0;
    }
  };

  const toggleSelectMember = (id: string) => {
    const newSet = new Set(selectedMembers);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedMembers(newSet);
  };

  const toggleSelectAll = () => {
    if (selectedMembers.size === filteredMembers.length) setSelectedMembers(new Set());
    else setSelectedMembers(new Set(filteredMembers.map(m => m.id)));
  };

  const openEditModal = (member: User) => {
    setSelectedMember(member);
    setEditForm({ username: member.username, phone: member.phone, role: member.role, remark: member.remark });
    setShowEditModal(true);
  };


  const loadStudentDetailData = async (userId: string) => {
    try {
      const { data: records } = await supabase
        .from('exam_records')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      const recordsData = records || [];
      setExamRecords(recordsData);

      if (recordsData.length > 0) {
        const totalCount = recordsData.length;
        const avgAccuracy = Math.round(
          recordsData.reduce((sum, r) => sum + (r.score / r.total_score * 100), 0) / totalCount
        );
        const recentTime = recordsData[0]?.created_at || '-';
        setExamStats({
          totalCount,
          avgAccuracy,
          recentStudyTime: recentTime,
        });
      } else {
        setExamStats({ totalCount: 0, avgAccuracy: 0, recentStudyTime: '-' });
      }

      // 从 teacher_students 表查询绑定关系
      const { data: teacherStudent } = await supabase
        .from('teacher_students')
        .select('teacher_id')
        .eq('student_id', userId)
        .maybeSingle();

      if (teacherStudent?.teacher_id) {
        const { data: teacher } = await supabase
          .from('profiles')
          .select('id, username, phone')
          .eq('id', teacherStudent.teacher_id)
          .maybeSingle();
        if (teacher) {
          setBindTeacherInfo({
            id: teacher.id,
            username: teacher.username,
            phone: teacher.phone?.includes('@') ? teacher.phone.split('@')[0] : teacher.phone
          });
        } else {
          setBindTeacherInfo(null);
        }
      } else {
        setBindTeacherInfo(null);
      }
    } catch {
    }
  };

  const handleUnbindTeacher = async () => {
    if (!selectedMember || !bindTeacherInfo) return;
    setUnbindingTeacher(true);
    try {
      const teacherId = bindTeacherInfo.id;

      // 删除绑定关系
      const { data: deleteData, error: deleteError } = await supabase
        .from('teacher_students')
        .delete()
        .eq('student_id', selectedMember.id)
        .select();
      if (deleteError || !deleteData || deleteData.length === 0) {
        setTeacherBindError('解绑失败，请稍后重试');
        setTimeout(() => setTeacherBindError(''), 3000);
      } else {
        setTeacherBindSuccess('解绑成功');
        setBindTeacherInfo(null);
        // 局部更新：直接移除该学员的绑定信息
        setStudentTeacherMap(prev => { const next = {...prev}; delete next[selectedMember.id]; return next; });
        setTimeout(() => setTeacherBindSuccess(''), 3000);
      }
    } catch {
      setTeacherBindError('解绑失败，请稍后重试');
      setTimeout(() => setTeacherBindError(''), 3000);
    }
    setUnbindingTeacher(false);
  };

  const handleBindTeacher = async () => {
    if (!selectedMember) return;
    if (!teacherBindForm.teacherPhone || !teacherBindForm.bindCode) {
      setTeacherBindError('请填写教师手机号和绑定码');
      setTimeout(() => setTeacherBindError(''), 3000);
      return;
    }
    setTeacherBindLoading(true);
    setTeacherBindError('');
    setTeacherBindSuccess('');
    try {
      // 1. 检查学员是否已绑定教师
      const { data: existingBind } = await supabase
        .from('teacher_students')
        .select('id')
        .eq('student_id', selectedMember.id)
        .maybeSingle();
      if (existingBind) {
        setTeacherBindError('该学员已绑定教师，请先解绑后再绑定新教师');
        setTimeout(() => setTeacherBindError(''), 3000);
        setTeacherBindLoading(false);
        return;
      }

      // 2. 查找教师账号
      const { data: teacherAccount } = await supabase
        .from('user_accounts')
        .select('auth_user_id, username, mobile')
        .eq('mobile', teacherBindForm.teacherPhone)
        .eq('role', 'teacher')
        .maybeSingle();
      if (!teacherAccount) {
        setTeacherBindError('未找到该手机号的教师账号');
        setTimeout(() => setTeacherBindError(''), 3000);
        setTeacherBindLoading(false);
        return;
      }
      const teacherId = teacherAccount.auth_user_id;

      // 3. 验证教师绑定码
      const { data: teacherBindCode } = await supabase
        .from('profiles')
        .select('bind_code')
        .eq('id', teacherId)
        .eq('bind_code', teacherBindForm.bindCode)
        .maybeSingle();
      if (!teacherBindCode) {
        setTeacherBindError('教师绑定码不正确');
        setTimeout(() => setTeacherBindError(''), 3000);
        setTeacherBindLoading(false);
        return;
      }

      // 4. 创建教师-学员绑定关系
      const { data: bindData, error: insertError } = await supabase
        .from('teacher_students')
        .insert({ teacher_id: teacherId, student_id: selectedMember.id, remark: '' })
        .select();
      if (insertError || !bindData || bindData.length === 0) {
        setTeacherBindError('绑定失败，请稍后重试');
        setTimeout(() => setTeacherBindError(''), 3000);
        setTeacherBindLoading(false);
        return;
      }

      // 5. 更新显示
      setTeacherBindSuccess(`绑定成功！教师：${teacherAccount.username}`);
      setTeacherBindForm({ teacherPhone: '', bindCode: '' });
      setBindTeacherInfo({
        id: teacherId,
        username: teacherAccount.username,
        phone: teacherAccount.mobile?.includes('@') ? teacherAccount.mobile.split('@')[0] : teacherAccount.mobile
      });
      // 局部更新：直接更新绑定关系映射
      setStudentTeacherMap(prev => ({
        ...prev,
        [selectedMember.id]: {
          id: teacherId,
          username: teacherAccount.username,
          phone: teacherAccount.mobile?.includes('@') ? teacherAccount.mobile.split('@')[0] : teacherAccount.mobile
        }
      }));
      setTimeout(() => setTeacherBindSuccess(''), 3000);
    } catch {
      setTeacherBindError('绑定失败，请稍后重试');
      setTimeout(() => setTeacherBindError(''), 3000);
    }
    setTeacherBindLoading(false);
  };

  const openDetailModal = async (member: User) => {
    setSelectedMember(member);
    setDetailRemark(member.remark || '');
    await loadStudentDetailData(member.id);
    setShowDetailModal(true);
  };

  const handleSaveRemark = async () => {
    if (!selectedMember) return;
    setSavingRemark(true);
    await userStore.updateUser(selectedMember.id, { remark: detailRemark });
    // 局部更新：直接更新 selectedMember 和 members 中的备注
    setSelectedMember(prev => prev ? { ...prev, remark: detailRemark } : prev);
    setMembers(prev => prev.map(m => m.id === selectedMember.id ? { ...m, remark: detailRemark } : m));
    setSavingRemark(false);
  };

  const startEditBasicInfo = () => {
    if (!selectedMember) return;
    let status = 'enabled';
    if (selectedMember.isBanned) status = 'banned';
    else if (selectedMember.isDisabled) status = 'disabled';
    setBasicInfoForm({
      username: selectedMember.username,
      phone: selectedMember.phone?.includes('@') ? selectedMember.phone.split('@')[0] : selectedMember.phone,
      status
    });
    setIsEditingBasicInfo(true);
  };

  const cancelEditBasicInfo = () => {
    setIsEditingBasicInfo(false);
  };

  const saveBasicInfo = async () => {
    if (!selectedMember) return;
    setSavingBasicInfo(true);
    const updates: Partial<User> = {
      username: basicInfoForm.username,
      phone: basicInfoForm.phone.includes('@') ? basicInfoForm.phone : `${basicInfoForm.phone}@meoo.local`
    };
    if (basicInfoForm.status === 'enabled') {
      updates.isDisabled = false;
      updates.isBanned = false;
    } else if (basicInfoForm.status === 'disabled') {
      updates.isDisabled = true;
      updates.isBanned = false;
    } else if (basicInfoForm.status === 'banned') {
      updates.isDisabled = false;
      updates.isBanned = true;
    }
    const success = await userStore.updateUser(selectedMember.id, updates);
    // 局部更新：直接更新 selectedMember 和 members 中的基本信息
    setSelectedMember(prev => prev ? { ...prev, ...updates } : prev);
    setMembers(prev => prev.map(m => m.id === selectedMember.id ? { ...m, ...updates } : m));
    setIsEditingBasicInfo(false);
    setSavingBasicInfo(false);
    if (success) {
      // 触发全局事件通知 Header 刷新用户信息
      window.dispatchEvent(new Event('user-info-updated'));
    }
  };

  const [orgs, setOrgs] = useState<Organization[]>([]);

  useEffect(() => {
    const loadCategories = async () => {
      await categoryStore.init();
      setOrgs(categoryStore.getOrganizations());
    };
    loadCategories();

    const unsubscribe = categoryStore.subscribe(() => {
      setOrgs(categoryStore.getOrganizations());
    });
    return unsubscribe;
  }, []);


  const filterTabs = [
    { key: 'all', label: '全部学员' },
    { key: 'paid', label: '付费学员' },
    { key: 'free', label: '免费学员' },
    { key: 'active', label: '活跃学员' },
    { key: 'inactive', label: '禁用账号' },
  ];

  const getStatusBadge = (m: User) => {
    if (m.isBanned) return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">封禁</span>;
    if (m.isDisabled) return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">禁用</span>;
    return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">启用</span>;
  };

  const getPaidBadge = (m: User) => {
    const now = new Date();
    const hasActivePackage = m.questionPackages.some(pkg => pkg.isActive && new Date(pkg.expireDate) > now);
    if (hasActivePackage) return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">付费学员</span>;
    return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">免费学员</span>;
  };

  const getPackageSourceLabel = (source?: string) => {
    switch (source) {
      case 'student_purchase':
        return { text: '自购', color: 'bg-green-100 text-green-700' };
      case 'admin_grant':
        return { text: '后台开通', color: 'bg-blue-100 text-blue-700' };
      case 'coupon_redeem':
        return { text: '券码兑换', color: 'bg-amber-100 text-amber-700' };
      default:
        return { text: '未知', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const getPackageStatusLabel = (pkg: QuestionPackage) => {
    const now = new Date();
    const expireDate = new Date(pkg.expireDate);
    if (!pkg.isActive) {
      return { text: '已关闭', color: 'bg-gray-100 text-gray-600' };
    }
    if (expireDate < now) {
      return { text: '已过期', color: 'bg-red-100 text-red-600' };
    }
    return { text: '生效中', color: 'bg-green-100 text-green-600' };
  };

  const handleDeletePackage = (pkg: QuestionPackage) => {
    setPackageToDelete(pkg);
    setDeleteSuccess(false);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeletePackage = async () => {
    if (!selectedMember || !packageToDelete) return;
    setDeleteLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const adminUsername = session?.user?.user_metadata?.username || '管理员';
    const result = await userStore.toggleQuestionPackage(selectedMember.id, packageToDelete.id, adminUsername);
    setDeleteLoading(false);
    if (result) {
      setDeleteSuccess(true);
      // 局部更新：直接更新 selectedMember 的 questionPackages
      setSelectedMember(prev => prev ? {
        ...prev,
        questionPackages: prev.questionPackages.map(p =>
          p.id === packageToDelete.id ? { ...p, isActive: false } : p
        )
      } : prev);
      // 同步更新 members 列表中对应学员的 questionPackages
      setMembers(prev => prev.map(m =>
        m.id === selectedMember.id ? {
          ...m,
          questionPackages: m.questionPackages.map(p =>
            p.id === packageToDelete.id ? { ...p, isActive: false } : p
          )
        } : m
      ));
      setTimeout(() => {
        setShowDeleteConfirmModal(false);
        setPackageToDelete(null);
        setDeleteSuccess(false);
      }, 1500);
    } else {
      alert('删除失败');
    }
  };

  const handleTogglePackage = (pkg: QuestionPackage) => {
    setPackageToToggle(pkg);
    setToggleSuccess(false);
    setShowToggleConfirmModal(true);
  };

  const confirmTogglePackage = async () => {
    if (!selectedMember || !packageToToggle) return;
    setToggleLoading(true);

    // 优先从 localStorage 获取管理员用户名
    let adminUsername = localStorage.getItem('admin_username') ||
                        localStorage.getItem('admin_user_name');
    let adminId: string | null = null;

    // 如果 localStorage 中没有，从 session 获取并查询数据库
    if (!adminUsername) {
      const { data: { session } } = await supabase.auth.getSession();
      adminId = session?.user?.id || null;
      if (adminId) {
        const { data: adminProfile } = await supabase
          .from('admin_users')
          .select('username')
          .eq('id', adminId)
          .maybeSingle();
        if (adminProfile?.username) {
          adminUsername = adminProfile.username;
        }
      }
    }

    // 如果都获取不到，使用 admin_id 兜底，而不是固定文字"管理员"
    if (!adminUsername) {
      adminUsername = adminId || '未知';
    }

    const success = await userStore.toggleQuestionPackage(selectedMember.id, packageToToggle.id, adminUsername);
    setToggleLoading(false);
    if (success) {
      setToggleSuccess(true);
      // 局部更新：直接更新 selectedMember 和 members 中对应套餐的 isActive
      const newIsActive = !packageToToggle.isActive;
      setSelectedMember(prev => prev ? {
        ...prev,
        questionPackages: prev.questionPackages.map(p =>
          p.id === packageToToggle.id ? { ...p, isActive: newIsActive } : p
        )
      } : prev);
      setMembers(prev => prev.map(m =>
        m.id === selectedMember.id ? {
          ...m,
          questionPackages: m.questionPackages.map(p =>
            p.id === packageToToggle.id ? { ...p, isActive: newIsActive } : p
          )
        } : m
      ));
      setTimeout(() => {
        setShowToggleConfirmModal(false);
        setPackageToToggle(null);
        setToggleSuccess(false);
      }, 1500);
    } else {
      alert('操作失败');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-blue-500" />学员列表</h2>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          {filterTabs.map(tab => (
            <button key={tab.key} onClick={() => setFilterType(tab.key as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterType === tab.key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-600'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input type="text" placeholder="搜索学员名或手机号..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="outline-none text-sm w-48" />
        </div>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" />添加学员
        </button>
        <div className="relative">
          <button onClick={() => setShowBatchActionMenu(!showBatchActionMenu)} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:border-gray-300 transition-colors">
            <MoreHorizontal className="w-4 h-4" />批量操作<ChevronDown className="w-3 h-3" />
          </button>
          <AnimatePresence>
            {showBatchActionMenu && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="absolute right-0 top-full mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 z-20">
                <button onClick={() => handleBatchAction('enable')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg">批量启用</button>
                <button onClick={() => handleBatchAction('disable')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">批量禁用</button>
                <button onClick={() => handleBatchAction('delete')} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">批量删除</button>
                <button onClick={() => handleBatchAction('export')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 last:rounded-b-lg">批量导出</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1500px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 text-left w-10"><input type="checkbox" checked={selectedMembers.size === filteredMembers.length && filteredMembers.length > 0} onChange={toggleSelectAll} className="rounded border-gray-300" /></th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">学员名</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">手机号</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">注册时间</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">最后登录</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">账号状态</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">U币余额</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">可用套餐</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">教师绑定</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">最近刷题</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">刷题次数</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600">客服备注</th>
                <th className="px-3 py-3 text-left text-sm font-medium text-gray-600 w-48">操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map(m => (
                <motion.tr key={m.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-3"><input type="checkbox" checked={selectedMembers.has(m.id)} onChange={() => toggleSelectMember(m.id)} className="rounded border-gray-300" /></td>
                  <td className="px-3 py-3">
                    <button onClick={() => openDetailModal(m)} className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                      {m.username}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-gray-700 font-mono text-sm">{m.phone?.includes('@') ? m.phone.split('@')[0] : m.phone}</td>
                  <td className="px-3 py-3 text-gray-500 text-sm">{formatDateTime(m.created_at)}</td>
                  <td className="px-3 py-3 text-gray-500 text-sm">{formatDateTime(m.last_login_time)}</td>
                  <td className="px-3 py-3">{getStatusBadge(m)}</td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => navigate(`/admin/point-transactions?userId=${m.id}&role=student&returnUrl=/admin/members`)}
                      className="text-orange-600 hover:text-orange-700 font-medium cursor-pointer hover:underline"
                    >
                      {memberPointsMap[m.id] || 0}
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      {(() => {
                        const activePackages = (m.questionPackages || []).filter(pkg => pkg.isActive && !isExpired(pkg.expireDate));
                        if (activePackages.length === 0) {
                          return <span className="text-gray-400 text-sm">-</span>;
                        }
                        return activePackages.map(pkg => (
                          <div key={pkg.id} className="text-xs px-2 py-1 rounded flex items-center gap-1 bg-green-50 text-green-700">
                            <span className={`px-1 py-0.5 text-[10px] rounded bg-purple-100 text-purple-600`}>
                              学
                            </span>
                            {pkg.organization}-{pkg.category}-{pkg.level_text}{pkg.durationType ? `-${pkg.durationType === 'month' ? '月包' : pkg.durationType === 'quarter' ? '季包' : pkg.durationType === 'halfYear' ? '半年包' : pkg.durationType === 'year' ? '年包' : pkg.durationType === 'custom' ? '自定义包' : ''}` : ''} (<span className="text-gray-600">{formatDate(pkg.expireDate)}到期</span>)
                          </div>
                        ));
                      })()}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {(() => {
                      const bindTeacher = studentTeacherMap[m.id];
                      if (bindTeacher) {
                        return (
                          <div className="flex flex-col">
                            <span className="text-purple-600 font-medium text-sm">{bindTeacher.username}</span>
                            <span className="text-gray-400 text-xs font-mono">{bindTeacher.phone}</span>
                          </div>
                        );
                      }
                      return <span className="text-gray-400 text-sm">未绑定</span>;
                    })()}
                  </td>
                  <td className="px-3 py-3 text-gray-500 text-sm">{formatDateTime(m.recentStudyTime)}</td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => navigate(`/exam-records?studentId=${m.id}&returnUrl=/admin/members`)}
                      className="text-blue-600 hover:text-blue-700 hover:underline font-medium cursor-pointer"
                      title="查看答题记录"
                    >
                      {m.studyCount || 0}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 max-w-24 truncate" title={m.remark}>{m.remark || '-'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openPointModal(m)} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded" title="U币管理"><Crown className="w-4 h-4" /></button>
                      <button onClick={() => openPackageModal(m)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title="管理刷题包"><BookOpen className="w-4 h-4" /></button>
                      <button onClick={() => handleClearRecords(m)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="清除刷题记录"><Eraser className="w-4 h-4" /></button>
                      <button onClick={() => { setSelectedMember(m); setResetPasswordSuccess(false); setShowResetPasswordModal(true); }} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="重置密码"><Key className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(m)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="删除"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredMembers.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>暂无学员数据</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDetailModal && selectedMember && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl p-6 w-full max-w-[900px] max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold flex items-center gap-2"><Shield className="w-5 h-5 text-blue-500" />学员详情 - {selectedMember.username}</h3>
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2"><UserIcon className="w-4 h-4 text-blue-500" />基本信息</h4>
                    {!isEditingBasicInfo ? (
                      <button onClick={startEditBasicInfo} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
                        <Edit3 className="w-3.5 h-3.5" />修改
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={cancelEditBasicInfo} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">取消</button>
                        <button onClick={saveBasicInfo} disabled={savingBasicInfo} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                          {savingBasicInfo ? <span className="animate-spin">⟳</span> : <Save className="w-3.5 h-3.5" />}保存
                        </button>
                      </div>
                    )}
                  </div>
                  {!isEditingBasicInfo ? (
                    <div className="space-y-4">
                      {/* 第一行：核心信息 */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">姓名</span>
                          <p className="font-semibold text-sm text-gray-800">{selectedMember.username}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">手机号</span>
                          <p className="font-medium text-sm font-mono text-gray-700">{selectedMember.phone?.includes('@') ? selectedMember.phone.split('@')[0] : selectedMember.phone}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">账号状态</span>
                          <p className="font-medium text-sm">{selectedMember.isBanned ? <span className="text-red-600">封禁</span> : selectedMember.isDisabled ? <span className="text-orange-600">禁用</span> : <span className="text-green-600">启用</span>}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">付费状态</span>
                          <p className="font-medium text-sm">{selectedMember.isPaid ? <span className="text-amber-600">付费学员</span> : <span className="text-blue-600">免费学员</span>}</p>
                        </div>
                      </div>
                      {/* 第二行：时间信息和U币 */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">学员ID</span>
                          <p className="font-medium text-sm text-gray-500 font-mono">{selectedMember.id}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">注册时间</span>
                          <p className="font-medium text-sm text-gray-700">{formatDateTime(selectedMember.created_at)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">最后登录</span>
                          <p className="font-medium text-sm text-gray-700">{formatDateTime(selectedMember.last_login_time)}</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <span className="text-gray-400 text-xs">U币余额</span>
                          <div className="flex items-center justify-between mt-1">
                            <p className="font-bold text-lg text-orange-600">{memberPointsMap[selectedMember.id] || 0}</p>
                            <button
                              onClick={() => navigate(`/admin/point-transactions?userId=${selectedMember.id}&role=student&returnUrl=/admin/members`)}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                            >
                              查看明细 →
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* 第三行：教师绑定 - 已绑定显示教师信息，未绑定显示绑定表单 */}
                      <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-xs">教师绑定</span>
                            {bindTeacherInfo && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">已绑定</span>
                            )}
                          </div>
                          {bindTeacherInfo && (
                            <button onClick={handleUnbindTeacher} disabled={unbindingTeacher} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs hover:bg-red-100 disabled:opacity-50 flex items-center gap-1">
                              {unbindingTeacher ? <span className="animate-spin">⟳</span> : <span>解绑</span>}
                            </button>
                          )}
                        </div>
                        <div className="mt-3">
                          {bindTeacherInfo ? (
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                  <span className="text-purple-600 font-semibold text-sm">{bindTeacherInfo.username?.charAt(0)}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-sm text-gray-800">{bindTeacherInfo.username}</p>
                                  <p className="text-xs text-gray-500 font-mono">{bindTeacherInfo.phone}</p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="教师手机号"
                                  value={teacherBindForm.teacherPhone}
                                  onChange={e => setTeacherBindForm({...teacherBindForm, teacherPhone: e.target.value})}
                                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <input
                                  type="text"
                                  placeholder="绑定码"
                                  value={teacherBindForm.bindCode}
                                  onChange={e => setTeacherBindForm({...teacherBindForm, bindCode: e.target.value})}
                                  className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  onClick={handleBindTeacher}
                                  disabled={teacherBindLoading}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                                >
                                  {teacherBindLoading ? <span className="animate-spin">⟳</span> : '绑定'}
                                </button>
                              </div>
                              {teacherBindError && (
                                <p className="text-xs text-red-600">{teacherBindError}</p>
                              )}
                              {teacherBindSuccess && (
                                <p className="text-xs text-green-600">{teacherBindSuccess}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <label className="block text-gray-500 text-xs mb-1">姓名</label>
                        <input type="text" value={basicInfoForm.username} onChange={e => setBasicInfoForm({...basicInfoForm, username: e.target.value})} className="w-full px-2 py-1.5 border rounded text-sm" />
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <label className="block text-gray-500 text-xs mb-1">手机号</label>
                        <input type="text" value={basicInfoForm.phone} onChange={e => setBasicInfoForm({...basicInfoForm, phone: e.target.value})} className="w-full px-2 py-1.5 border rounded text-sm" />
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <label className="block text-gray-500 text-xs mb-1">账号状态</label>
                        <select value={basicInfoForm.status} onChange={e => setBasicInfoForm({...basicInfoForm, status: e.target.value})} className="w-full px-2 py-1.5 border rounded text-sm">
                          <option value="enabled">启用</option>
                          <option value="disabled">禁用</option>
                        </select>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-sm"><span className="text-gray-500 text-xs">学员ID</span><p className="font-medium text-sm text-gray-600">{selectedMember.id}</p></div>
                      <div className="bg-white rounded-lg p-3 shadow-sm"><span className="text-gray-500 text-xs">注册时间</span><p className="font-medium text-sm">{formatDateTime(selectedMember.created_at)}</p></div>
                      <div className="bg-white rounded-lg p-3 shadow-sm"><span className="text-gray-500 text-xs">最后登录</span><p className="font-medium text-sm">{formatDateTime(selectedMember.last_login_time)}</p></div>
                      <div className="bg-white rounded-lg p-3 shadow-sm"><span className="text-gray-500 text-xs">角色</span><p className="font-medium text-sm">{selectedMember.role === 'teacher' ? '教师' : '学员'}</p></div>
                      <div className="bg-white rounded-lg p-3 shadow-sm"><span className="text-gray-500 text-xs">付费状态</span><p className="font-medium text-sm">{selectedMember.isPaid ? <span className="text-amber-600">付费学员</span> : <span className="text-blue-600">免费学员</span>}</p></div>
                      <div className="bg-white rounded-lg p-3 shadow-sm">
                        <span className="text-gray-500 text-xs">教师绑定</span>
                        <div className="mt-2">
                          {bindTeacherInfo ? (
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-sm text-purple-600">{bindTeacherInfo.username}</p>
                                <p className="text-xs text-gray-500 font-mono">{bindTeacherInfo.phone}</p>
                              </div>
                              <button onClick={handleUnbindTeacher} disabled={unbindingTeacher} className="ml-2 px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200 disabled:opacity-50">
                                {unbindingTeacher ? '解绑中...' : '解绑'}
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder="教师手机号"
                                  value={teacherBindForm.teacherPhone}
                                  onChange={e => setTeacherBindForm({...teacherBindForm, teacherPhone: e.target.value})}
                                  className="flex-1 px-2 py-1.5 border rounded text-sm"
                                />
                                <input
                                  type="text"
                                  placeholder="绑定码"
                                  value={teacherBindForm.bindCode}
                                  onChange={e => setTeacherBindForm({...teacherBindForm, bindCode: e.target.value})}
                                  className="w-20 px-2 py-1.5 border rounded text-sm"
                                />
                                <button
                                  onClick={handleBindTeacher}
                                  disabled={teacherBindLoading}
                                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {teacherBindLoading ? '绑定中...' : '绑定'}
                                </button>
                              </div>
                              {teacherBindError && (
                                <p className="text-xs text-red-600">{teacherBindError}</p>
                              )}
                              {teacherBindSuccess && (
                                <p className="text-xs text-green-600">{teacherBindSuccess}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-500" />订单记录</h4>
                  <OrderList type="admin-student" targetUid={selectedMember?.id} mode="modal" enabled={!!selectedMember} />
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-green-500" />答题记录</h4>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                      <p className="text-2xl font-bold text-green-600">{examStats.totalCount}</p>
                      <p className="text-xs text-gray-500">累计刷题次数</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                      <p className="text-2xl font-bold text-blue-600">{examStats.avgAccuracy}%</p>
                      <p className="text-xs text-gray-500">平均正确率</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm text-center">
                      <p className="text-lg font-bold text-purple-600">{formatDateTime(examStats.recentStudyTime)}</p>
                      <p className="text-xs text-gray-500">最近刷题时间</p>
                    </div>
                  </div>
                  {examRecords.length > 0 && (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {examRecords.slice(0, 5).map((record, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-2 shadow-sm flex items-center justify-between text-sm">
                          <span className="font-medium">{record.paper_title}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-500">{Math.round(record.score / record.total_score * 100)}%</span>
                            <span className="text-gray-400">{formatDateTime(record.completed_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><BookOpen className="w-4 h-4 text-purple-500" />可用套餐 ({(selectedMember.questionPackages || []).filter(pkg => pkg.isActive && new Date(pkg.expireDate) > new Date()).length})</h4>
                  {(selectedMember.questionPackages || []).filter(pkg => pkg.isActive && new Date(pkg.expireDate) > new Date()).length === 0 ? (
                    <p className="text-gray-400 text-sm">暂无可用的套餐</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(selectedMember.questionPackages || []).filter(pkg => pkg.isActive && new Date(pkg.expireDate) > new Date()).map(pkg => {
                        const sourceLabel = getPackageSourceLabel(pkg.packageSource);
                        const statusLabel = getPackageStatusLabel(pkg);
                        return (
                          <div key={pkg.id} className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm truncate">{pkg.organization} - {pkg.category} - {pkg.level_text}{pkg.durationType ? `-${pkg.durationType === 'month' ? '月包' : pkg.durationType === 'quarter' ? '季包' : pkg.durationType === 'halfYear' ? '半年包' : pkg.durationType === 'year' ? '年包' : pkg.durationType === 'custom' ? '自定义包' : ''}` : ''}</span>
                                  <span className={`px-2 py-0.5 text-xs rounded ${sourceLabel.color}`}>{sourceLabel.text}</span>
                                  <span className={`px-2 py-0.5 text-xs rounded ${statusLabel.color}`}>{statusLabel.text}</span>
                                  <span className="text-xs text-gray-500">到期: {formatDate(pkg.expireDate)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleTogglePackage(pkg)}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title={pkg.isActive ? '关闭' : '开启'}
                                >
                                  {pkg.isActive ? <Ban className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-5 border border-gray-300">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><BookOpen className="w-4 h-4 text-gray-500" />历史套餐 ({(selectedMember.questionPackages || []).filter(pkg => !pkg.isActive || new Date(pkg.expireDate) <= new Date()).length})</h4>
                  {(selectedMember.questionPackages || []).filter(pkg => !pkg.isActive || new Date(pkg.expireDate) <= new Date()).length === 0 ? (
                    <p className="text-gray-500 text-sm">暂无历史套餐</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(selectedMember.questionPackages || []).filter(pkg => !pkg.isActive || new Date(pkg.expireDate) <= new Date()).map(pkg => {
                        const sourceLabel = getPackageSourceLabel(pkg.packageSource);
                        const statusLabel = getPackageStatusLabel(pkg);
                        const getOperatorLabel = () => {
                          if (pkg.deletedBy) return pkg.deletedBy;
                          if (pkg.closedBy) return pkg.closedBy;
                          if (!pkg.isActive && new Date(pkg.expireDate) <= new Date()) return '系统';
                          return '-';
                        };
                        return (
                          <div key={pkg.id} className="bg-white rounded-lg p-3 shadow-sm">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm truncate">{pkg.organization} - {pkg.category} - {pkg.level_text}</span>
                              <span className={`px-2 py-0.5 text-xs rounded ${sourceLabel.color}`}>{sourceLabel.text}</span>
                              <span className={`px-2 py-0.5 text-xs rounded ${statusLabel.color}`}>{statusLabel.text}</span>
                              <span className="text-xs text-gray-500">到期: {formatDate(pkg.expireDate)}</span>
                              <span className="text-xs text-gray-400">操作人：{getOperatorLabel()}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-5 border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-gray-500" />客服备注</h4>
                  <textarea
                    value={detailRemark}
                    onChange={e => setDetailRemark(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm min-h-[80px]"
                    placeholder="请输入客服备注..."
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleSaveRemark}
                      disabled={savingRemark}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {savingRemark ? <span className="animate-spin">⟳</span> : <CheckCircle className="w-4 h-4" />}
                      保存备注
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && selectedMember && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl p-6 w-[450px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">编辑学员</h3>
                <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">学员名</label><input type="text" value={editForm.username} onChange={e => setEditForm({...editForm, username: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">手机号</label><input type="text" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">客服备注</label><textarea value={editForm.remark} onChange={e => setEditForm({...editForm, remark: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={3} /></div>
                <div className="flex gap-3">
                  <button onClick={() => setShowEditModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleEdit} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">保存</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 使用通用开通套餐组件 */}
      <PackageGrantModal
        showPackageModal={showPackageModal}
        setShowPackageModal={setShowPackageModal}
        showConfirmPackageModal={showConfirmPackageModal}
        setShowConfirmPackageModal={setShowConfirmPackageModal}
        userName={selectedMember?.username || ''}
        userLabel="学员姓名"
        packageForm={packageForm}
        setPackageForm={setPackageForm}
        orgs={orgs}
        availableCategories={availableCategories}
        availableLevels={availableLevels}
        handleOrgChange={handleOrgChange}
        handleCategoryChange={handleCategoryChange}
        handleLevelChange={handleLevelChange}
        onPackageTypeChange={handlePackageTypeChange}
        isChecking={isChecking}
        hasPaidPapers={hasPaidPapers}
        validationError={validationError}
        onConfirm={handleAddPackage}
      />

      <AnimatePresence>
        {showDeleteConfirmModal && packageToDelete && selectedMember && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl p-6 w-[450px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-500" />
                  确认删除刷题包
                </h3>
                <button onClick={() => setShowDeleteConfirmModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              {!deleteSuccess ? (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800 font-medium mb-2">您即将删除以下刷题包：</p>
                    <div className="bg-white rounded p-3 border border-red-100">
                      <p className="text-sm"><span className="text-gray-500">学员：</span><span className="font-medium">{selectedMember.username}</span></p>
                      <p className="text-sm mt-1"><span className="text-gray-500">刷题包：</span><span className="font-medium">{packageToDelete.organization} - {packageToDelete.category} - {packageToDelete.level_text}</span></p>
                      <p className="text-sm mt-1"><span className="text-gray-500">来源：</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${getPackageSourceLabel(packageToDelete.packageSource).color}`}>
                          {getPackageSourceLabel(packageToDelete.packageSource).text}
                        </span>
                      </p>
                      <p className="text-sm mt-1"><span className="text-gray-500">到期时间：</span><span className="font-medium">{formatDate(packageToDelete.expireDate)}</span></p>
                    </div>
                    <p className="text-sm text-red-600 mt-3 font-medium">⚠️ 删除后该学员将立即失去此刷题权限，且无法恢复！</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowDeleteConfirmModal(false)} disabled={deleteLoading} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">取消</button>
                    <button onClick={confirmDeletePackage} disabled={deleteLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                      {deleteLoading ? <span className="animate-spin">⟳</span> : null}
                      确认删除
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">删除成功</span>
                    </div>
                    <p className="text-sm text-green-700">刷题包已成功删除，学员权限已失效。</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteMemberModal && memberToDelete && (
          <AuthConfirmModal
            isOpen={showDeleteMemberModal}
            title="确认删除学员"
            message={`确定要删除学员"${memberToDelete.username}"吗？此操作不可恢复，相关的答题记录、套餐等数据也将被删除。`}
            onConfirm={confirmDeleteMember}
            onCancel={() => setShowDeleteMemberModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showClearRecordsModal && memberToClear && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowClearRecordsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Eraser className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">确认清除刷题记录</h3>
              </div>
              <p className="text-gray-600 mb-4">
                确定要清除学员<span className="text-blue-600 font-bold text-lg mx-1">{memberToClear.username}</span>的所有刷题记录吗？此操作将删除该学员的所有答题记录、编程题提交记录和评分反馈，但会保留学员的基础信息和套餐权限。
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">请输入鉴权密码</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="password"
                    value={clearRecordsPassword}
                    onChange={(e) => { setClearRecordsPassword(e.target.value); setClearRecordsError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !clearingRecords) confirmClearRecords(); }}
                    placeholder="请输入您的鉴权密码"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                {clearRecordsError && <p className="text-sm text-red-600 mt-1">{clearRecordsError}</p>}
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowClearRecordsModal(false)} disabled={clearingRecords} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">取消</button>
                <button onClick={confirmClearRecords} disabled={clearingRecords || !clearRecordsPassword.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {clearingRecords ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>清除中...</>) : '确认清除'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showToggleConfirmModal && packageToToggle && selectedMember && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl p-6 w-[450px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  {packageToToggle.isActive ? <Ban className="w-5 h-5 text-orange-500" /> : <CheckCircle className="w-5 h-5 text-green-500" />}
                  确认{packageToToggle.isActive ? '关闭' : '开通'}刷题包
                </h3>
                <button onClick={() => setShowToggleConfirmModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              {!toggleSuccess ? (
                <div className="space-y-4">
                  <div className={`${packageToToggle.isActive ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} border rounded-lg p-4`}>
                    <p className={`text-sm font-medium mb-2 ${packageToToggle.isActive ? 'text-orange-800' : 'text-green-800'}`}>您即将{packageToToggle.isActive ? '关闭' : '开通'}以下刷题包：</p>
                    <div className="bg-white rounded p-3 border border-gray-100">
                      <p className="text-sm"><span className="text-gray-500">学员：</span><span className="font-medium">{selectedMember.username}</span></p>
                      <p className="text-sm mt-1"><span className="text-gray-500">刷题包：</span><span className="font-medium">{packageToToggle.organization} - {packageToToggle.category} - {packageToToggle.level_text}</span></p>
                      <p className="text-sm mt-1"><span className="text-gray-500">来源：</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${getPackageSourceLabel(packageToToggle.packageSource).color}`}>
                          {getPackageSourceLabel(packageToToggle.packageSource).text}
                        </span>
                      </p>
                      <p className="text-sm mt-1"><span className="text-gray-500">到期时间：</span><span className="font-medium">{formatDate(packageToToggle.expireDate)}</span></p>
                    </div>
                    <p className={`text-sm mt-3 font-medium ${packageToToggle.isActive ? 'text-orange-600' : 'text-green-600'}`}>
                      {packageToToggle.isActive ? '⚠️ 关闭后学员将暂时失去刷题权限，但可重新开通。' : '✓ 开通后学员将恢复刷题权限。'}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowToggleConfirmModal(false)} disabled={toggleLoading} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">取消</button>
                    <button onClick={confirmTogglePackage} disabled={toggleLoading} className={`flex-1 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 ${packageToToggle.isActive ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}>
                      {toggleLoading ? <span className="animate-spin">⟳</span> : null}
                      确认{packageToToggle.isActive ? '关闭' : '开通'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">{packageToToggle.isActive ? '关闭' : '开通'}成功</span>
                    </div>
                    <p className="text-sm text-green-700">刷题包已成功{packageToToggle.isActive ? '关闭' : '开通'}。</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showResetPasswordModal && selectedMember && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl p-6 w-[400px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">重置密码</h3>
                <button onClick={() => setShowResetPasswordModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              {!resetPasswordSuccess ? (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">学员: {selectedMember.username}</p>
                    <p className="text-sm text-amber-800 mt-1">手机号: {selectedMember.phone?.includes('@') ? selectedMember.phone.split('@')[0] : selectedMember.phone}</p>
                    <p className="text-sm text-amber-600 mt-2">确定要将密码重置为默认密码 <span className="font-mono font-bold">{DEFAULT_PASSWORD}</span> 吗？</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowResetPasswordModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">取消</button>
                    <button onClick={async () => {
                      // 通过云函数重置密码（同时更新 Supabase Auth 和 user_accounts）
                      // 使用 fetch 直接调用，确保传递有效的 Authorization header
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        const accessToken = session?.access_token;

                        const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                          },
                          body: JSON.stringify({
                            action: 'reset_password',
                            teacherId: selectedMember.id
                          }),
                        });

                        const result = await response.json();

                        if (response.ok && result.success) {
                          const adminId = session?.user?.id || 'admin';
                          addLog(adminId, '管理员', 'super', '重置学员密码', selectedMember.username, selectedMember.phone);
                          setResetPasswordSuccess(true);
                        } else {
                          alert('密码重置失败：' + (result.error || result.message || '未知错误'));
                        }
                      } catch (err) {
                        console.error('重置密码请求失败:', err);
                        alert('密码重置失败：网络请求异常');
                      }
                    }} className="flex-1 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">确认重置</button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">密码重置成功</span>
                    </div>
                    <p className="text-sm text-green-700">学员 <span className="font-medium">{selectedMember.username}</span> 的密码已重置为:</p>
                    <p className="text-lg font-mono font-bold text-green-800 mt-2 text-center bg-white rounded py-2">{DEFAULT_PASSWORD}</p>
                    <p className="text-xs text-green-600 mt-2">已发送站内通知告知学员</p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowResetPasswordModal(false)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">完成</button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-xl p-6 w-[400px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">添加学员</h3>
                <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">学员名</label><input type="text" value={addForm.username} onChange={e => setAddForm({...addForm, username: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="请输入学员名" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">手机号</label><input type="text" value={addForm.phone} onChange={e => setAddForm({...addForm, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg" placeholder="请输入手机号" /></div>
                <div className="flex gap-3">
                  <button onClick={() => setShowAddModal(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">取消</button>
                  <button onClick={handleAddUser} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">创建</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* U币管理弹窗 */}
      {showPointModal && pointMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Crown className="w-5 h-5 text-orange-500" />
                U币管理 - {pointMember.username}
              </h3>
              <button onClick={() => setShowPointModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {pointSuccess ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-800 mb-2">操作成功</h4>
                <p className="text-gray-500 mb-4">
                  {pointAction === 'add' ? '增加' : pointAction === 'deduct' ? '扣减' : '赠送'}
                  U币 {pointAmount} 已完成
                </p>
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <span className="text-sm text-gray-600">当前U币余额：</span>
                  <span className="text-2xl font-bold text-orange-600 ml-2">{pointBalance}</span>
                </div>
                <button
                  onClick={() => { setShowPointModal(false); }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  完成
                </button>
              </div>
            ) : (
              <>
                {/* 当前U币余额 */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">当前U币余额</span>
                    <span className="text-3xl font-bold text-orange-600">{pointBalance}</span>
                  </div>
                </div>

                {/* 操作类型选择 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">操作类型</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setPointAction('add')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        pointAction === 'add'
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-green-300'
                      }`}
                    >
                      增加U币
                    </button>
                    <button
                      onClick={() => setPointAction('deduct')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        pointAction === 'deduct'
                          ? 'bg-red-50 border-red-500 text-red-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-red-300'
                      }`}
                    >
                      减少U币
                    </button>
                    <button
                      onClick={() => setPointAction('gift')}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        pointAction === 'gift'
                          ? 'bg-purple-50 border-purple-500 text-purple-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'
                      }`}
                    >
                      赠送U币
                    </button>
                  </div>
                </div>

                {/* U币数量输入 */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    U币数量 {pointAction === 'deduct' && <span className="text-red-500">(扣减后余额不能为负)</span>}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={pointAmount || ''}
                    onChange={(e) => setPointAmount(Math.max(0, parseInt(e.target.value) || 0))}
                    placeholder="请输入U币数量"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-500"
                  />
                  {pointAction === 'deduct' && pointAmount > pointBalance && (
                    <p className="text-red-500 text-xs mt-1">扣减数量不能超过当前余额 {pointBalance}</p>
                  )}
                </div>

                {/* 操作备注 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">操作备注</label>
                  <textarea
                    value={pointRemark}
                    onChange={(e) => setPointRemark(e.target.value)}
                    placeholder="请输入操作原因或备注信息..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500"
                  />
                </div>

                {/* 操作预览 */}
                {pointAmount > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-6 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">操作后余额预估：</span>
                      <span className={`font-bold ${
                        pointAction === 'add' || pointAction === 'gift' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {pointAction === 'deduct' ? pointBalance - pointAmount : pointBalance + pointAmount}
                      </span>
                    </div>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex gap-3">
                  <button
                    onClick={handlePointAction}
                    disabled={pointLoading || pointAmount <= 0 || (pointAction === 'deduct' && pointAmount > pointBalance)}
                    className={`flex-1 py-2 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                      pointAction === 'add' ? 'bg-green-600 hover:bg-green-700' :
                      pointAction === 'deduct' ? 'bg-red-600 hover:bg-red-700' :
                      'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {pointLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        处理中...
                      </span>
                    ) : (
                      `确认${pointAction === 'add' ? '增加' : pointAction === 'deduct' ? '减少' : '赠送'} ${pointAmount} U币`
                    )}
                  </button>
                  <button
                    onClick={() => setShowPointModal(false)}
                    className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    取消
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
